import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';

import { adminAPI } from '../../services/api';
import { usePermissions } from '../../context/PermissionsContext';

import {
  MessageSquare, User, Clock, CheckCircle, X, Send,
  AlertCircle, ChevronRight
} from 'lucide-react';

const statusColors = {
  open: '#3b82f6',
  in_progress: COLORS.primary,
  resolved: COLORS.success,
  closed: '#6b7280',
};

const statusLabels = {
  open: 'Открыт',
  in_progress: 'В работе',
  resolved: 'Решён',
  closed: 'Закрыт',
};

const AdminSupport = () => {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (isSuperAdmin || hasPermission('support', 'view')) {
      loadTickets();
      const interval = setInterval(loadTickets, 30000);
      return () => clearInterval(interval);
    }
  }, [statusFilter, isSuperAdmin]);

  const loadTickets = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.getTickets(params);
      setTickets(res.data || []);
    } catch (err) {
      console.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  // Access control - must be after all hooks
  if (!isSuperAdmin && !hasPermission('support', 'view')) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: COLORS.textSecondary 
      }}>
        <MessageSquare size={64} style={{ marginBottom: '16px', opacity: 0.5 }} />
        <h2 style={{ color: COLORS.text, marginBottom: '8px' }}>Доступ запрещён</h2>
        <p>У вас нет прав для просмотра тикетов поддержки</p>
      </div>
    );
  }

  const selectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyText('');
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      await adminAPI.replyToTicket(selectedTicket.id, replyText);
      setReplyText('');
      loadTickets();
      // Refresh selected ticket
      const updated = tickets.find(t => t.id === selectedTicket.id);
      if (updated) {
        selectTicket({ ...updated, messages: [...(updated.messages || []), { message: replyText, sender_role: 'admin', created_at: new Date().toISOString() }] });
      }
    } catch (err) {
      console.error('Failed to send reply');
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await adminAPI.updateTicketStatus(ticketId, status);
      loadTickets();
    } catch (err) {
      console.error('Failed to update status');
    }
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', gap: '16px' }}>
      {/* Tickets List */}
      <div style={{
        width: selectedTicket ? '350px' : '100%',
        backgroundColor: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}` }}>
          <h2 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0, marginBottom: '12px' }}>
            Поддержка
          </h2>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: COLORS.background,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
            }}
          >
            <option value="">Все тикеты</option>
            <option value="open">Открытые</option>
            <option value="in_progress">В работе</option>
            <option value="resolved">Решённые</option>
            <option value="closed">Закрытые</option>
          </select>
        </div>

        {/* Tickets */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>Загрузка...</div>
          ) : tickets.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>Нет тикетов</div>
          ) : (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => selectTicket(ticket)}
                style={{
                  padding: '14px 20px',
                  borderBottom: `1px solid ${COLORS.border}20`,
                  cursor: 'pointer',
                  backgroundColor: selectedTicket?.id === ticket.id ? COLORS.background : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                    {ticket.subject}
                  </div>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: `${statusColors[ticket.status]}20`,
                    color: statusColors[ticket.status],
                    fontSize: '11px',
                    fontWeight: '600',
                  }}>
                    {statusLabels[ticket.status]}
                  </span>
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                  {ticket.client_email} • {ticket.client_account}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                  {new Date(ticket.updated_at).toLocaleString('ru')}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Panel */}
      {selectedTicket && (
        <div style={{
          flex: 1,
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Chat Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600' }}>
                {selectedTicket.subject}
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                {selectedTicket.client_name || selectedTicket.client_email}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(isSuperAdmin || hasPermission('support', 'close')) && (
                <select
                  value={selectedTicket.status}
                  onChange={(e) => updateStatus(selectedTicket.id, e.target.value)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: `${statusColors[selectedTicket.status]}20`,
                    border: `1px solid ${statusColors[selectedTicket.status]}`,
                    borderRadius: '8px',
                    color: statusColors[selectedTicket.status],
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  <option value="open">Открыт</option>
                  <option value="in_progress">В работе</option>
                  <option value="resolved">Решён</option>
                  <option value="closed">Закрыт</option>
                </select>
              )}
              <button
                onClick={() => setSelectedTicket(null)}
                style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
            {selectedTicket.messages?.map((msg, i) => {
              const isAdmin = msg.sender_role !== 'user';
              return (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    maxWidth: '70%',
                    padding: '12px 16px',
                    backgroundColor: isAdmin ? COLORS.primary : COLORS.background,
                    color: isAdmin ? '#000' : COLORS.text,
                    borderRadius: '12px',
                    borderBottomRightRadius: isAdmin ? '4px' : '12px',
                    borderBottomLeftRadius: isAdmin ? '12px' : '4px',
                  }}>
                    <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                      {msg.message}
                    </div>
                    <div style={{ fontSize: '11px', opacity: 0.7, textAlign: 'right' }}>
                      {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply Input - only show if user has reply permission */}
          {(isSuperAdmin || hasPermission('support', 'reply')) && (
            <div style={{
              padding: '16px 20px',
              borderTop: `1px solid ${COLORS.border}`,
              display: 'flex',
              gap: '12px',
            }}>
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Напишите ответ..."
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
                onKeyPress={(e) => e.key === 'Enter' && sendReply()}
              />
              <button
                onClick={sendReply}
                disabled={!replyText.trim()}
                style={{
                  padding: '12px 20px',
                  backgroundColor: replyText.trim() ? COLORS.primary : COLORS.border,
                  border: 'none',
                  borderRadius: '8px',
                  color: replyText.trim() ? '#000' : COLORS.textSecondary,
                  cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Send size={18} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSupport;
